import { Link } from 'react-router-dom';
import { shell } from '../../shell/shellClasses';
import type { BusinessHealth } from './types';

function bandClass(band: BusinessHealth['band']): string {
  if (band === 'safe') return 'text-[var(--shell-success)]';
  if (band === 'alert') return 'text-[var(--shell-warning)]';
  if (band === 'critical') return 'text-[var(--shell-danger)]';
  return shell.muted;
}

function bandWord(band: BusinessHealth['band']): string {
  if (band === 'safe') return 'Healthy';
  if (band === 'alert') return 'Needs attention';
  if (band === 'critical') return 'At risk';
  return 'Not enough data';
}

/**
 * The 3-second answer: one score, one word, the drivers behind it.
 * Data-quality telemetry (confidence, coverage, domain quality) is engineering
 * surface and lives in the trust chip / developer views, not here.
 */
export function HealthCards({ health }: { health: BusinessHealth }) {
  const unavailable = health.overall == null;

  return (
    <section aria-labelledby="biz-health-title" className="space-y-4">
      <h2 id="biz-health-title" className="sr-only">
        Business health
      </h2>
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <p
          className={`text-6xl font-semibold leading-none tabular-nums tracking-tight sm:text-7xl ${bandClass(
            health.band,
          )}`}
        >
          {unavailable ? '—' : health.overall}
        </p>
        <div className="min-w-0 pb-1">
          <p className={`text-lg font-medium ${bandClass(health.band)}`}>
            {bandWord(health.band)}
          </p>
          <p className={`text-sm ${shell.muted}`}>
            {unavailable
              ? 'Not enough connected data yet to score your business.'
              : 'Business health, out of 100'}
          </p>
        </div>
      </div>

      <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {health.components.map((c) => (
          <li key={c.id} className="flex items-baseline gap-2 text-sm">
            <span className="font-semibold tabular-nums text-[var(--shell-fg)]">
              {c.score == null ? '—' : c.score}
            </span>
            {c.href ? (
              <Link to={c.href} className={`${shell.muted} hover:text-[var(--shell-accent)]`}>
                {c.label}
              </Link>
            ) : (
              <span className={shell.muted}>{c.label}</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
